<template>
  <div
    class="rating"
    :title="hint"
    :style="ratingStyles"
    :class="{ rating_oneStar: oneStar }"
    role="img"
    :aria-label="`Рейтинг: ${value} из 5`"
  >
    <span class="wrap-stars">
      <StarIcon
        v-for="star in stars"
        :key="star"
        :class="{ filled: star <= Math.floor(value) }"
      />
    </span>
  </div>
</template>

<script setup lang="ts">
  import { StarIcon } from '@/shared/ui/icons/starIcon';

  interface Props {
    hint?: string;
    oneStar?: boolean;
    size?: number;
    value: number;
  }

  const props = defineProps<Props>();

  const value = computed(() => Math.min(Math.max(props.value, 0), 5)); // Ограничиваем значение
  const stars = computed(() => Array.from({ length: props.oneStar ? 1 : 5 }, (_, i) => i + 1)); // Массив звёзд

  const clipValue = computed(() => {
    const split = value.value % 1;
    const position = split ? Math.ceil(split * 8) * 12.5 : 0; // Обрезка на 8 интервалов

    return split ? `${position}%` : '100%';
  });

  const ratingStyles = computed(() => ({
    '--last-clip-position': clipValue.value,
    '--size': props.size ? `${props.size}px` : '12px',
  }));
</script>

<style lang="scss" scoped>
  .rating {
    --size: 12px;
    --gap: calc(var(--size) / 6);
    display: inline-grid;
    height: var(--size);

    .wrap-stars {
      display: grid;
      grid-area: 1 / 1 / 2 / 2;
      grid-template: 1fr / repeat(5, var(--size));
      gap: var(--gap);
    }

    .icon {
      --clip-position: 100%;
      --fill: var(--alias-color-icon-subtlest);
      width: var(--size);
      height: var(--size);
      fill: var(--fill);
      clip-path: polygon(0 0, var(--clip-position) 0, var(--clip-position) 100%, 0% 100%);

      &:nth-last-child(1) {
        --clip-position: var(--last-clip-position);
      }

      &.filled {
        --fill: var(--alias-color-icon-accent-red);
        clip-path: none;
      }
    }

    &.rating_oneStar {
      .icon:nth-last-child(1) {
        --clip-position: 100%;
      }

      .wrap-stars {
        grid-template: 1fr / 1fr;
      }
    }
  }
</style>
